// src/components/finance/steps/AddressStep.jsx
import React, { useState } from 'react';
import '../../../styles/LoanSteps.css';

const AddressStep = ({ data, onNext, onPrevious, loading }) => {
  const [formData, setFormData] = useState({
    pincode: data.pincode || '',
    city: data.city || '',
    state: data.state || '',
    address: data.address || '',
    residence_type: data.residence_type || '',
    residence_duration_months: data.residence_duration_months || ''
  });

  const [errors, setErrors] = useState({});

  const residenceTypes = [
    { value: 'rented', label: 'Rented' },
    { value: 'self_owned', label: 'Self-owned' },
    { value: 'owned_by_spouse_parents_sibling', label: 'Owned by spouse / parents / sibling' },
    { value: 'relative_family_owned', label: 'Relative / family-owned' },
    { value: 'pg_accommodation', label: 'PG accommodation' },
    { value: 'government_accommodation', label: 'Government accommodation' },
    { value: 'university_accommodation', label: 'University accommodation' }
  ];

  const durationOptions = [
    { value: '12', label: 'More than or equal to 12 months' },
    { value: '9', label: 'Between 6 to 11 months' },
    { value: '3', label: 'Less than 6 months' }
  ];

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    setErrors(prev => ({ ...prev, [field]: '' }));
  };

  const validate = () => {
    const newErrors = {};
    if (!/^\d{6}$/.test(formData.pincode)) newErrors.pincode = 'Valid 6-digit pincode required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.state.trim()) newErrors.state = 'State is required';
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.residence_type) newErrors.residence_type = 'Residence type is required';
    if (!formData.residence_duration_months) newErrors.residence_duration_months = 'Duration is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validate()) onNext(formData);
  };

  return (
    <div className="step-container">
      <div className="step-header">
        <h3>Current address details</h3>
        <p>Help us check if we are operational at your location</p>
      </div>

      <form onSubmit={handleSubmit} className="step-form">
        {/* Pincode + City */}
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="pincode">Pincode</label>
            <input
              id="pincode"
              type="text"
              value={formData.pincode}
              onChange={(e) => handleChange('pincode', e.target.value)}
              placeholder="6-digit pincode"
              maxLength={6}
              className={errors.pincode ? 'error' : ''}
            />
            {errors.pincode && <span className="error-text">{errors.pincode}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="city">City</label>
            <input
              id="city"
              type="text"
              value={formData.city}
              onChange={(e) => handleChange('city', e.target.value)}
              placeholder="City name"
              className={errors.city ? 'error' : ''}
            />
            {errors.city && <span className="error-text">{errors.city}</span>}
          </div>
        </div>

        {/* State */}
        <div className="form-group">
          <label htmlFor="state">State</label>
          <input
            id="state"
            type="text"
            value={formData.state}
            onChange={(e) => handleChange('state', e.target.value)}
            placeholder="State name"
            className={errors.state ? 'error' : ''}
          />
          {errors.state && <span className="error-text">{errors.state}</span>}
        </div>

        {/* Address */}
        <div className="form-group">
          <label htmlFor="address">House No./Street/Locality</label>
          <input
            id="address"
            type="text"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="Full address"
            className={errors.address ? 'error' : ''}
          />
          {errors.address && <span className="error-text">{errors.address}</span>}
        </div>

        {/* Residence Type */}
        <div className="form-group">
          <label htmlFor="residence_type">Current residence ownership type</label>
          <select
            id="residence_type"
            value={formData.residence_type}
            onChange={(e) => handleChange('residence_type', e.target.value)}
            className={errors.residence_type ? 'error' : ''}
          >
            <option value="">Select residence type</option>
            {residenceTypes.map(type => (
              <option key={type.value} value={type.value}>{type.label}</option>
            ))}
          </select>
          {errors.residence_type && <span className="error-text">{errors.residence_type}</span>}
        </div>

        {/* Duration — Radio Group */}
        <div className="form-group">
          <label>Duration at current residence</label>
          <p className="field-description">How long have you been residing at the current address?</p>
          <div className="radio-group">
            {durationOptions.map(option => (
              /* ✅ FIX: onClick on the wrapper div — pura row clickable */
              <div
                key={option.value}
                className="radio-label"
                onClick={() => handleChange('residence_duration_months', option.value)}
                style={{
                  borderColor: formData.residence_duration_months === option.value ? '#003566' : undefined,
                  background: formData.residence_duration_months === option.value ? '#F0F4FA' : undefined,
                }}
              >
                <input
                  type="radio"
                  name="residence_duration"
                  value={option.value}
                  checked={formData.residence_duration_months === option.value}
                  onChange={() => handleChange('residence_duration_months', option.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <span>{option.label}</span>
              </div>
            ))}
          </div>
          {errors.residence_duration_months && (
            <span className="error-text">{errors.residence_duration_months}</span>
          )}
        </div>

        {/* Buttons */}
        <div className="button-group">
          <button type="button" onClick={onPrevious} className="previous-button">
            Previous
          </button>
          <button type="submit" className="next-button" disabled={loading}>
            {loading ? 'Processing...' : 'Next'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AddressStep;